import * as React from "react"
import { toast as sonnerToast } from "sonner" // Import sonner's toast

// Import context for automatic category detection
let getToastContext: (() => { category: string; businessName?: string }) | null = null;

// Function to set the context getter (called by ToastProvider)
export const setToastContextGetter = (getter: () => { category: string; businessName?: string }) => {
  getToastContext = getter;
};

type ToasterToast = {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: {
    label: React.ReactNode;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }; // Changed to match Sonner's ToastAction
  timestamp: number
  type?: "default" | "success" | "error" | "info" | "warning";
  businessName?: string; // The actual business name
  category?: string; // The category/module (e.g., "Business Management", "Employee Dashboard")
  count?: number; // For deduplication - how many times this toast was triggered
}

const TOAST_LIMIT = Number.MAX_SAFE_INTEGER // Changed to store all toasts

// Deduplication settings
const DEDUP_WINDOW_MS = 2000; // Time window for deduplication (2 seconds)
const recentToasts = new Map<string, { timestamp: number; count: number; sonnerToastId: string | number }>();

// Generate a dedup key from toast content
function getDeduplicationKey(title: React.ReactNode, description?: React.ReactNode, type?: string): string {
  const titleStr = typeof title === 'string' ? title : String(title);
  const descStr = typeof description === 'string' ? description : String(description || '');
  return `${type || 'default'}:${titleStr}:${descStr}`;
}

// Check if toast should be deduplicated, returns existing toast info if duplicate
function checkDeduplication(key: string): { isDuplicate: boolean; existingToastId?: string | number; newCount?: number } {
  const now = Date.now();
  const existing = recentToasts.get(key);

  // Clean up old entries
  for (const [k, v] of recentToasts.entries()) {
    if (now - v.timestamp > DEDUP_WINDOW_MS) {
      recentToasts.delete(k);
    }
  }

  if (existing && now - existing.timestamp < DEDUP_WINDOW_MS) {
    existing.count++;
    existing.timestamp = now;
    return { isDuplicate: true, existingToastId: existing.sonnerToastId, newCount: existing.count };
  }

  return { isDuplicate: false };
}

// Register a new toast for deduplication tracking
function registerToast(key: string, sonnerToastId: string | number): void {
  recentToasts.set(key, { timestamp: Date.now(), count: 1, sonnerToastId });
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = {
  title?: React.ReactNode;
  description?: React.ReactNode | (() => React.ReactNode); // Allow function for description
  action?: {
    label: React.ReactNode;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  }; // Changed to match Sonner's ToastAction
  type?: "default" | "success" | "error" | "info" | "warning";
  businessName?: string; // The actual business name
  category?: string; // The category/module (e.g., "Business Management", "Employee Dashboard")
};

// This function only dispatches to our internal state for logging
function dispatchToastToLog({ title, description, action, type, businessName, category }: Toast) {
  const id = genId();
  const resolvedDescription = typeof description === 'function' ? description() : description;

  dispatch({
    type: "ADD_TOAST",
    toast: {
      id,
      timestamp: Date.now(),
      title,
      description: resolvedDescription,
      action,
      type,
      businessName,
      category,
    },
  });
}

// Helper to get automatic context
const getAutoContext = () => {
  if (getToastContext) {
    try {
      return getToastContext();
    } catch (e) {
      // Context not available, return empty
      return { category: undefined, businessName: undefined };
    }
  }
  return { category: undefined, businessName: undefined };
};

// Extended options type for our toast wrapper
type ToastOptions = Parameters<typeof sonnerToast>[1] & {
  businessName?: string;
  category?: string;
  dedupe?: boolean; // Set to false to disable deduplication for this toast
};

// Helper to show toast with deduplication
function showToastWithDedup(
  title: React.ReactNode,
  options: ToastOptions | undefined,
  type: "default" | "success" | "error" | "info" | "warning",
  sonnerMethod: typeof sonnerToast | typeof sonnerToast.success
) {
  const autoContext = getAutoContext();
  const finalOptions = {
    ...options,
    category: options?.category || autoContext.category,
    businessName: options?.businessName || autoContext.businessName,
  };

  // Check for deduplication (enabled by default)
  const shouldDedupe = finalOptions.dedupe !== false;
  const dedupKey = getDeduplicationKey(title, finalOptions?.description, type);

  if (shouldDedupe) {
    const dedupResult = checkDeduplication(dedupKey);

    if (dedupResult.isDuplicate && dedupResult.existingToastId) {
      // Update the existing toast with count
      const countSuffix = dedupResult.newCount && dedupResult.newCount > 1
        ? ` (×${dedupResult.newCount})`
        : '';
      const updatedTitle = typeof title === 'string' ? `${title}${countSuffix}` : title;

      sonnerToast.dismiss(dedupResult.existingToastId);
      const newToastId = sonnerMethod(updatedTitle, options);
      if (newToastId) {
        recentToasts.set(dedupKey, {
          timestamp: Date.now(),
          count: dedupResult.newCount || 1,
          sonnerToastId: newToastId
        });
      }

      // Update log entry
      dispatchToastToLog({
        title: updatedTitle,
        description: finalOptions?.description,
        action: finalOptions?.action as Toast['action'],
        type,
        businessName: finalOptions?.businessName,
        category: finalOptions?.category
      });
      return newToastId;
    }
  }

  // Show new toast
  const toastId = sonnerMethod(title, options);
  if (toastId && shouldDedupe) {
    registerToast(dedupKey, toastId);
  }

  dispatchToastToLog({
    title,
    description: finalOptions?.description,
    action: finalOptions?.action as Toast['action'],
    type,
    businessName: finalOptions?.businessName,
    category: finalOptions?.category
  });

  return toastId;
}

// This is the main toast object that will be exported and used by the app
const toast = Object.assign(
  (title: React.ReactNode, options?: ToastOptions) => {
    return showToastWithDedup(title, options, "default", sonnerToast);
  },
  {
    success: (title: React.ReactNode, options?: ToastOptions) => {
      return showToastWithDedup(title, options, "success", sonnerToast.success);
    },
    error: (title: React.ReactNode, options?: ToastOptions) => {
      return showToastWithDedup(title, options, "error", sonnerToast.error);
    },
    info: (title: React.ReactNode, options?: ToastOptions) => {
      return showToastWithDedup(title, options, "info", sonnerToast.info);
    },
    warning: (title: React.ReactNode, options?: ToastOptions) => {
      return showToastWithDedup(title, options, "warning", sonnerToast.warning);
    },
    // Promise helper for async operations
    promise: <T,>(
      promise: Promise<T> | (() => Promise<T>),
      messages: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((error: unknown) => string);
      },
      options?: ToastOptions
    ) => {
      const autoContext = getAutoContext();
      const finalOptions = {
        ...options,
        category: options?.category || autoContext.category,
        businessName: options?.businessName || autoContext.businessName,
      };

      // Log the loading state
      dispatchToastToLog({
        title: messages.loading,
        type: "info",
        businessName: finalOptions?.businessName,
        category: finalOptions?.category
      });

      return sonnerToast.promise(promise, {
        loading: messages.loading,
        success: (data) => {
          const successMsg = typeof messages.success === 'function'
            ? messages.success(data)
            : messages.success;
          dispatchToastToLog({
            title: successMsg,
            type: "success",
            businessName: finalOptions?.businessName,
            category: finalOptions?.category
          });
          return successMsg;
        },
        error: (err) => {
          const errorMsg = typeof messages.error === 'function'
            ? messages.error(err)
            : messages.error;
          dispatchToastToLog({
            title: errorMsg,
            type: "error",
            businessName: finalOptions?.businessName,
            category: finalOptions?.category
          });
          return errorMsg;
        },
      });
    },
    // Dismiss a specific toast or all toasts
    dismiss: (toastId?: string | number) => {
      sonnerToast.dismiss(toastId);
    },
    // Loading toast (useful for manual promise handling)
    loading: (title: React.ReactNode, options?: ToastOptions) => {
      const autoContext = getAutoContext();
      const finalOptions = {
        ...options,
        category: options?.category || autoContext.category,
        businessName: options?.businessName || autoContext.businessName,
      };
      const toastId = sonnerToast.loading(title, options);
      dispatchToastToLog({
        title,
        description: finalOptions?.description,
        type: "info",
        businessName: finalOptions?.businessName,
        category: finalOptions?.category
      });
      return toastId;
    },
  }
);

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  // Filter toasts to only include those from the last 24 hours
  const recentToastsFiltered = state.toasts.filter(
    (t) => Date.now() - t.timestamp < 24 * 60 * 60 * 1000
  );

  // Group toasts by type for statistics
  const stats = React.useMemo(() => {
    const grouped = {
      total: recentToastsFiltered.length,
      success: 0,
      error: 0,
      warning: 0,
      info: 0,
      default: 0,
    };
    recentToastsFiltered.forEach((t) => {
      const type = t.type || 'default';
      grouped[type]++;
    });
    return grouped;
  }, [recentToastsFiltered]);

  // Get toasts grouped by category
  const byCategory = React.useMemo(() => {
    const grouped: Record<string, ToasterToast[]> = {};
    recentToastsFiltered.forEach((t) => {
      const category = t.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(t);
    });
    return grouped;
  }, [recentToastsFiltered]);

  return {
    ...state,
    toasts: recentToastsFiltered,
    stats,
    byCategory,
    // Remove a specific toast from history
    removeToast: (toastId?: string) => dispatch({ type: "REMOVE_TOAST", toastId }),
    // Clear all toast history
    clearHistory: () => dispatch({ type: "REMOVE_TOAST" }),
    // Get only error toasts (useful for debugging)
    errorToasts: recentToastsFiltered.filter((t) => t.type === 'error'),
    // Check if there are any recent errors
    hasErrors: recentToastsFiltered.some((t) => t.type === 'error'),
  };
}

// Export type for external use
export type { ToasterToast, ToastOptions };
export { useToast, toast };
